/**
 * @fileoverview Assignment Interface Component
 * 
 * Main interface for displaying and managing knowledge base file assignments.
 * Provides file-to-parrot assignment controls with status indicators.
 * 
 * Requirements: 4.1, 4.2, 4.3
 */

import React, { useMemo } from 'react';
import { ASSIGNMENT_STATUS_CONFIG, SYNC_STATUS_CONFIG } from '../types.js';

/**
 * Assignment Interface Component
 * 
 * @param {Object} props - Component props
 * @param {Array} props.files - Knowledge base files
 * @param {Array} props.parrots - Available parrots
 * @param {Object} props.assignments - File assignments (fileId -> assignments[])
 * @param {Object} props.syncStatuses - Sync statuses (fileId -> parrotId -> status)
 * @param {Set} props.selectedFiles - Selected file IDs
 * @param {Set} props.expandedFiles - Expanded file IDs
 * @param {Object} props.config - Module configuration
 * @param {function} props.onAssignmentToggle - Assignment toggle handler
 * @param {function} props.onFileSelection - File selection handler
 * @param {function} props.onFileExpansion - File expansion handler
 * @param {function} props.getAssignmentStatus - Get assignment status function
 * @param {function} props.getSyncStatus - Get sync status function
 * @param {Object} props.filterOptions - Filter options
 * @param {function} props.onFilterChange - Filter change handler
 * @param {string} [props.className] - CSS class name
 */
export const AssignmentInterface = ({
  files,
  parrots,
  assignments,
  syncStatuses,
  selectedFiles,
  expandedFiles,
  config,
  onAssignmentToggle,
  onFileSelection,
  onFileExpansion,
  getAssignmentStatus,
  getSyncStatus,
  filterOptions,
  onFilterChange,
  className=''
}) => {
  // Get file icon based on MIME type
  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('text')) return '📝';
    if (mimeType?.includes('word')) return '📄';
    if (mimeType?.includes('html')) return '🌐';
    return '📄';
  };

  // Get assignment summary for a file
  const getAssignmentSummary = (fileId) => {
    const fileAssignments = assignments[fileId] || [];
    const assignedParrots = fileAssignments.filter(a => a.status !== 'removed');
    return {
      total: assignedParrots.length,
      synced: assignedParrots.filter(a => a.status === 'synced').length,
      syncing: assignedParrots.filter(a => a.status === 'syncing').length,
      failed: assignedParrots.filter(a => a.status === 'failed').length
    };
  };

  // Check if assignment exists and get its status
  const isAssigned = (fileId, parrotId) => {
    const fileAssignments = assignments[fileId] || [];
    const assignment = fileAssignments.find(a => a.parrotId === parrotId);
    return assignment && assignment.status !== 'removed';
  };

  // Render filter controls
  const renderFilterControls = () => (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6 dark:bg-gray-900">
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">
            Status:
          </label>
          <select
            value={filterOptions.status}
            onChange={(e) => onFilterChange({ ...filterOptions, status: e.target.value })}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white dark:bg-gray-800"
          >
            <option value="all">All</option>
            {Object.entries(ASSIGNMENT_STATUS_CONFIG).map(([status, config]) => (
              <option key={status} value={status}>{config.label}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">
            Parrot:
          </label>
          <select
            value={filterOptions.parrot}
            onChange={(e) => onFilterChange({ ...filterOptions, parrot: e.target.value })}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white dark:bg-gray-800"
          >
            <option value="all">All Parrots</option>
            {parrots.map(parrot => (
              <option key={parrot.id} value={parrot.id}>{parrot.name}</option>
            ))}
          </select>
        </div>

        {config.showSyncStatus && (
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">
              Sync Status:
            </label>
            <select
              value={filterOptions.syncStatus}
              onChange={(e) => onFilterChange({ ...filterOptions, syncStatus: e.target.value })}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white dark:bg-gray-800"
            >
              <option value="all">All</option>
              {Object.entries(SYNC_STATUS_CONFIG).map(([status, config]) => (
                <option key={status} value={status}>{config.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );

  // Render assignment status badge
  const renderStatusBadge = (status, syncStatus = null) => {
    const statusConfig = ASSIGNMENT_STATUS_CONFIG[status];
    const syncConfig = syncStatus ? SYNC_STATUS_CONFIG[syncStatus.status] : null;
    
    return (
      <div className="flex items-center space-x-1">
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            statusConfig.color === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' :
            statusConfig.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200' :
            statusConfig.color === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200' :
            statusConfig.color === 'red' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200' :
            'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
          }`}
          title={statusConfig.description}
        >
          <span className="mr-1">{statusConfig.icon}</span>
          {statusConfig.label}
        </span>
        
        {syncConfig && (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              syncConfig.color === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' :
              syncConfig.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200' :
              syncConfig.color === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200' :
              syncConfig.color === 'red' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200' :
              'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
            }`}
            title={syncConfig.description}
          >
            <span className="mr-1">{syncConfig.icon}</span>
            {syncConfig.label}
          </span>
        )}
      </div>
    );
  };

  // Show empty state if no files
  if (files.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="text-6xl mb-4">📄</div>
        <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2 dark:text-gray-600">
          No Knowledge Base Files
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          Upload some files to your knowledge base to assign them to parrots.
        </p>
      </div>
    );
  }

  // Show empty state if no parrots
  if (parrots.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="text-6xl mb-4">🦜</div>
        <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2 dark:text-gray-600">
          No Parrots Found
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          Create your first Parrot to assign knowledge base files.
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Filter controls */}
      {renderFilterControls()}

      {/* Files list */}
      <div className="space-y-4">
        {files.map(file => {
          const fileAssignments = assignments[file.id] || [];
          const isExpanded = expandedFiles.has(file.id);
          const isSelected = selectedFiles.has(file.id);
          const summary = getAssignmentSummary(file.id);
          
          return (
            <div
              key={file.id}
              className={`bg-white dark:bg-gray-800 rounded-lg border transition-colors ${
                isSelected 
                  ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/10' 
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              {/* File header */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {config.allowBatchOperations && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => onFileSelection(file.id, e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-teal-600 focus:ring-teal-500 dark:bg-gray-800"
                      />
                    )}
                    
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{getFileIcon(file.mimeType)}</span>
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {file.name || 'Unnamed File'}
                        </h4>
                        <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                          <span>{file.type || 'Unknown Type'}</span>
                          {file.size && (
                            <>
                              <span>•</span>
                              <span>{file.formattedSize || `${Math.round(file.size / 1024)} KB`}</span>
                            </>
                          )}
                          <span>•</span>
                          <span>{summary.total} assignment{summary.total !== 1 ? 's' : ''}</span>
                          {/* Show assigned parrot names */}
                          {fileAssignments.length > 0 && (
                            <>
                              <span>•</span>
                              <span className="text-teal-600 dark:text-teal-400">
                                {fileAssignments.slice(0, 2).map(a => a.parrotName).join(', ')}
                                {fileAssignments.length > 2 && ` +${fileAssignments.length - 2} more`}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {/* Assignment summary */}
                    <div className="flex items-center space-x-1">
                      {summary.synced > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                          ✅ {summary.synced}
                        </span>
                      )}
                      {summary.syncing > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                          🔄 {summary.syncing}
                        </span>
                      )}
                      {summary.failed > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200">
                          ❌ {summary.failed}
                        </span>
                      )}
                    </div>
                    
                    <button
                      onClick={() => onFileExpansion(file.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors dark:text-gray-300 dark:text-gray-600"
                    >
                      <svg 
                        className={`h-5 w-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded assignment details */}
              {isExpanded && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {parrots.map(parrot => {
                      const assigned = isAssigned(file.id, parrot.id);
                      const assignmentStatus = getAssignmentStatus(file.id, parrot.id);
                      const syncStatus = getSyncStatus(file.id, parrot.id);
                      
                      return (
                        <div
                          key={parrot.id}
                          className={`p-3 rounded-lg border transition-colors ${
                            assigned
                              ? 'border-teal-300 bg-teal-50 dark:bg-teal-900/20'
                              : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={assigned}
                                onChange={() => onAssignmentToggle(file.id, parrot.id, assigned)}
                                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-teal-600 focus:ring-teal-500 dark:bg-gray-800"
                              />
                              <span className="font-medium text-gray-900 dark:text-white">
                                {parrot.name}
                              </span>
                            </div>
                          </div>
                          
                          {/* Assignment and sync status */}
                          {assigned && assignmentStatus && (
                            <div className="space-y-2">
                              {renderStatusBadge(assignmentStatus, syncStatus)}
                              
                              {syncStatus && syncStatus.error && (
                                <div className="text-xs text-red-600 dark:text-red-400">
                                  Error: {syncStatus.error}
                                </div>
                              )}
                              
                              {syncStatus && syncStatus.progress !== undefined && syncStatus.progress < 100 && (
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${syncStatus.progress}%` }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
